import { useMutation, useQuery, useQueryClient } from "@organizacaox/lai-design-system/query";
import {
  approveHoldBatch,
  createIngestionKey,
  discardHoldBatch,
  getHoldBatch,
  getIngestionKeys,
  getIngestionStatuses,
  getPendingHold,
  getVolumeGuard,
  revokeIngestionKey,
  saveVolumeGuard,
  type IngestionKey,
  type VolumeGuardConfig,
} from "@/api/ingestion";

// `pendingHold` e `holdBatch` começam com o prefixo de `volumeGuard`: invalidar
// `volumeGuard` depois de uma decisão atualiza os três.
export const ingestionKeys = {
  keys: ["ingestion", "keys"] as const,
  statuses: ["ingestion", "statuses"] as const,
  volumeGuard: ["ingestion", "volume-guard"] as const,
  pendingHold: ["ingestion", "volume-guard", "pending"] as const,
  holdBatch: (id: string) => ["ingestion", "volume-guard", "batch", id] as const,
};

export function useIngestionKeys() {
  return useQuery({ queryKey: ingestionKeys.keys, queryFn: getIngestionKeys });
}

export function useIngestionStatuses() {
  return useQuery({ queryKey: ingestionKeys.statuses, queryFn: getIngestionStatuses });
}

export function useCreateIngestionKey(
  handlers: {
    onSuccess?: (k: IngestionKey & { key: string }) => void;
    onError?: (e: Error) => void;
  } = {},
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string }) => createIngestionKey(input),
    onSuccess: (created) => {
      handlers.onSuccess?.(created);
      void qc.invalidateQueries({ queryKey: ingestionKeys.keys });
    },
    onError: handlers.onError,
  });
}

// Diferente da tela do CRM, a revogada NÃO some: vai para "Revogadas (n)".
export function useRevokeIngestionKey(
  handlers: { onSuccess?: () => void; onError?: (e: Error) => void } = {},
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => revokeIngestionKey(id),
    onSuccess: () => {
      handlers.onSuccess?.();
      void qc.invalidateQueries({ queryKey: ingestionKeys.keys });
    },
    onError: handlers.onError,
  });
}

export function useVolumeGuard() {
  return useQuery({ queryKey: ingestionKeys.volumeGuard, queryFn: getVolumeGuard });
}

// O aviso global consulta a cada minuto: a rajada acontece fora da tela.
export function usePendingHold(enabled: boolean) {
  return useQuery({
    queryKey: ingestionKeys.pendingHold,
    queryFn: getPendingHold,
    enabled,
    refetchInterval: 60_000,
  });
}

export function useHoldBatch(id: string | null) {
  return useQuery({
    queryKey: ingestionKeys.holdBatch(id ?? ""),
    queryFn: () => getHoldBatch(id as string),
    enabled: id !== null,
    // Lote pendente ainda pode crescer: o número do diálogo de descarte
    // precisa acompanhar.
    refetchInterval: 15_000,
  });
}

function useInvalidateVolumeGuard() {
  const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: ingestionKeys.volumeGuard });
}

export function useSaveVolumeGuard(
  handlers: { onSuccess?: () => void; onError?: (e: Error) => void } = {},
) {
  const invalidate = useInvalidateVolumeGuard();
  return useMutation({
    mutationFn: (c: VolumeGuardConfig) => saveVolumeGuard(c),
    onSuccess: () => {
      handlers.onSuccess?.();
      invalidate();
    },
    onError: handlers.onError,
  });
}

export function useApproveHoldBatch(
  handlers: { onSuccess?: () => void; onError?: (e: Error) => void } = {},
) {
  const invalidate = useInvalidateVolumeGuard();
  return useMutation({
    mutationFn: (id: string) => approveHoldBatch(id),
    onSuccess: () => {
      handlers.onSuccess?.();
      invalidate();
    },
    onError: handlers.onError,
  });
}

export function useDiscardHoldBatch(
  handlers: {
    onSuccess?: (r: { discarded: number; kept: number }) => void;
    onError?: (e: Error) => void;
  } = {},
) {
  const invalidate = useInvalidateVolumeGuard();
  return useMutation({
    mutationFn: (v: { id: string; expectedCount: number }) =>
      discardHoldBatch(v.id, v.expectedCount),
    onSuccess: (r) => {
      handlers.onSuccess?.(r);
      invalidate();
    },
    onError: handlers.onError,
  });
}
